import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useBackNavigate } from '@/shared/hooks/use-back-navigate';
import {
  Button,
  Modal,
  PageContainer,
  Table,
  Tabs,
} from '@react-apps/design-system';
import type { TabItem } from '@react-apps/design-system';
import type { Invoice } from '../../domain/entities/sales.entity';
import {
  useBillingAgreement,
  useContract,
  useContractInvoices,
} from '../hooks/use-contract-details';
import { BillingAgreementPanel } from '../components/BillingAgreementPanel';
import { ContractBillingAgreementsModal } from '../components/ContractBillingAgreementsModal';
import { OfferCard } from '../components/OfferCard';
import { EntityEventsSection } from '../components/EntityEventsSection';
import { useRouteTabs } from '@/shared/hooks/use-route-tabs';
import { EntityLink } from '@/shared/components/entity-link';
import { formatDocumentForDisplay } from '@/shared/utils/br-fields';
import { buildInvoiceColumns, InvoiceSummary } from '../shared/invoices';
import { ManualSettlementModal } from '../components/sales-manual-operation-modals';
import { SyncChargeHistoryModal } from '../components/sales-action-modals';
import {
  ContractStatusBadge,
  ContractTotalValue,
  ErrorState,
  Field,
  Mono,
  SectionCard,
  SkeletonCard,
  formatDateTime,
  tenantLabel,
} from '../shared/display';

/** Ids das abas — também são os segmentos da URL (`/contracts/:id/:tab`). */
const CONTRACT_TAB_IDS = ['detail', 'user', 'billing-agreement', 'audit'];

export function ContractDetailPage() {
  const { contractId } = useParams<{ contractId: string }>();
  const goBack = useBackNavigate('/contracts');
  const id = contractId ?? '';
  const { activeTab, onTabChange } = useRouteTabs(
    `/contracts/${id}`,
    CONTRACT_TAB_IDS,
  );

  const tabs: TabItem[] = [
    {
      id: 'detail',
      label: 'Detalhes',
      content: <DetailTab contractId={id} />,
    },
    {
      id: 'user',
      label: 'Dados do Usuário',
      content: <UserDataTab contractId={id} />,
    },
    {
      id: 'billing-agreement',
      label: 'Billing Agreement',
      content: <BillingAgreementTab contractId={id} />,
    },
    {
      // Só os eventos do Contract: os da BA moram na página dela.
      id: 'audit',
      label: 'Auditoria',
      content: <EntityEventsSection entityType="Contract" entityId={id} />,
    },
  ];

  return (
    <PageContainer maxWidth="4xl">
      <button
        onClick={goBack}
        className="text-(--color-text-secondary) hover:text-(--color-text-primary) flex items-center gap-1.5 text-sm transition-colors"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 19.5 8.25 12l7.5-7.5"
          />
        </svg>
        Contratos
      </button>

      {/* Aba ativa vem da URL (`/contracts/:id/:tab`) — link compartilhável. */}
      <Tabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />
    </PageContainer>
  );
}

// --- Aba: Detalhe ---

function DetailTab({ contractId }: { contractId: string }) {
  const { data: detail, isLoading, isError } = useContract(contractId);
  const { data: invoicesData, isLoading: invoicesLoading } =
    useContractInvoices(contractId);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [settlingInvoice, setSettlingInvoice] = useState<Invoice | null>(null);
  const [syncChargesOpen, setSyncChargesOpen] = useState(false);
  const invoiceColumns = buildInvoiceColumns(
    (invoice) => setSelectedInvoice(invoice),
    setSettlingInvoice,
  );

  if (isLoading) return <SkeletonCard rows={6} />;
  if (isError || !detail)
    return <ErrorState message="Contrato não encontrado." />;

  const { contract, offer, tenant } = detail;
  const invoices = invoicesData?.invoices ?? [];
  // O total vem somado do servidor, não de um `reduce` aqui: fatura placeholder
  // tem `value: null` e um `?? 0` a contava como grátis, subestimando o
  // contrato inteiro. O resumo também sabe dizer o que está estimado ou faltando.
  const valueSummary = invoicesData?.valueSummary;

  return (
    <div className="space-y-6">
      <SectionCard
        title="Resumo do Contrato"
        action={<ContractStatusBadge status={contract.status} />}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="ID" value={<Mono>{contract.id}</Mono>} />
          <Field label="Produto" value={offer?.productName ?? null} />
          <Field label="BU" value={tenantLabel(tenant)} />
          <Field
            label="Valor Total do Contrato"
            value={
              valueSummary ? (
                <ContractTotalValue summary={valueSummary} />
              ) : null
            }
          />
          <Field
            label="Iniciada em"
            value={formatDateTime(contract.startDate)}
          />
          {/* `endDate` é preenchido em QUALQUER status terminal (finished,
              incompleted, abandoned, cancelled) — condicionar ao campo, não ao
              status, senão os outros três ficam sem data alguma. Quem cancela é
              a BA: a data de cancelamento fica na aba Billing Agreement. */}
          <Field
            label="Finalizada em"
            value={contract.endDate ? formatDateTime(contract.endDate) : null}
          />
          <Field
            label="Atualizada em"
            value={formatDateTime(contract.updatedAt)}
          />
        </div>
      </SectionCard>

      {/* Os ids da oferta ficam aqui, não no resumo: `offer.id`/`externalId`
          identificam a OFERTA, e ler isso como "id do produto" já causou
          confusão — o produto só aparece no `providerMetadata`. */}
      <OfferCard offer={offer} />

      {/* Só as faturas ativas de todas as BAs do contrato — o mesmo conjunto que
          compõe o total acima. Substituídas/canceladas ficam na página da BA. */}
      <SectionCard
        title="Faturas do Contrato"
        // O botão só existe quando o servidor diz que alguma BA do contrato
        // tem o que buscar no gateway — e sincroniza só essas, não todas.
        action={
          invoicesData?.needsChargeHistorySync ? (
            <Button
              variant="secondary"
              onClick={() => setSyncChargesOpen(true)}
            >
              Sincronizar cobranças
            </Button>
          ) : undefined
        }
      >
        <Table
          columns={invoiceColumns}
          data={invoices}
          keyExtractor={(row) => row.id}
          loading={invoicesLoading}
          emptyMessage="Nenhuma invoice encontrada."
          striped
        />
      </SectionCard>

      <Modal
        open={selectedInvoice !== null}
        onClose={() => setSelectedInvoice(null)}
        title="Resumo da Fatura"
        size="lg"
      >
        {selectedInvoice && <InvoiceSummary invoice={selectedInvoice} />}
      </Modal>

      {settlingInvoice && (
        <ManualSettlementModal
          open
          onClose={() => setSettlingInvoice(null)}
          invoice={settlingInvoice}
          invalidateKey={['sales', 'contract', contractId]}
        />
      )}

      <SyncChargeHistoryModal
        invalidateKey={['sales', 'contract', contractId]}
        target={{ kind: 'contract', contractId }}
        open={syncChargesOpen}
        onClose={() => setSyncChargesOpen(false)}
      />
    </div>
  );
}

// --- Aba: Dados do Usuário ---

function UserDataTab({ contractId }: { contractId: string }) {
  const { data: detail, isLoading, isError } = useContract(contractId);

  if (isLoading) return <SkeletonCard rows={4} />;
  if (isError || !detail)
    return <ErrorState message="Contrato não encontrado." />;

  const { user } = detail;

  return (
    <SectionCard title="Dados do Usuário">
      {!user ? (
        <p className="text-(--color-text-secondary) text-sm italic">
          Usuário não encontrado.
        </p>
      ) : (
        <div className="flex items-start gap-4">
          <div className="bg-(--color-accent)/10 text-(--color-accent) flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-lg font-bold">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nome" value={user.name} />
            <Field label="E-mail" value={user.email} />
            <Field
              label="Documento"
              value={formatDocumentForDisplay(user.doc)}
            />
            <Field
              label="ID do Usuário"
              value={
                <Mono>
                  <EntityLink kind="user" id={user.userId} />
                </Mono>
              }
            />
            <Field label="ID da Conta" value={<Mono>{user.accountId}</Mono>} />
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// --- Aba: Billing Agreement ---

/**
 * A BA **atual** do contrato. O card e as ações vivem no `BillingAgreementPanel`,
 * compartilhado com a página `/billing-agreements/:id` — aqui só somamos o botão
 * que abre o histórico de BAs do contrato.
 */
function BillingAgreementTab({ contractId }: { contractId: string }) {
  const [agreementsOpen, setAgreementsOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [settlingInvoice, setSettlingInvoice] = useState<Invoice | null>(null);
  const { data: detail, isLoading, isError } = useContract(contractId);
  // Mesma query que o `BillingAgreementPanel` já dispara internamente
  // (`useBillingAgreement`) — o cache do react-query dedupe, não dobra a
  // request.
  const baId = detail?.billingAgreement?.id ?? '';
  const { data: baDetail, isLoading: invoicesLoading } =
    useBillingAgreement(baId);

  if (isLoading) return <SkeletonCard rows={6} />;
  if (isError || !detail)
    return <ErrorState message="Contrato não encontrado." />;

  const ba = detail.billingAgreement;

  return (
    <div className="space-y-6">
      {ba ? (
        <>
          <BillingAgreementPanel
            billingAgreement={ba}
            offer={detail.offer}
            invalidateKey={['sales', 'contract', contractId]}
            extraAction={
              <Button
                variant="secondary"
                onClick={() => setAgreementsOpen(true)}
              >
                Ver acordos de pagamento
              </Button>
            }
          />

          <SectionCard title="Faturas desta Billing Agreement">
            <Table
              columns={buildInvoiceColumns(
                setSelectedInvoice,
                setSettlingInvoice,
                // Escopo de uma BA só, então o preço vigente pode estimar os
                // ciclos ainda não cobrados.
                baDetail?.valueSummary.perCycleValue,
              )}
              data={baDetail?.invoices ?? []}
              keyExtractor={(row) => row.id}
              loading={invoicesLoading}
              emptyMessage="Nenhuma invoice encontrada."
              striped
            />
          </SectionCard>
        </>
      ) : (
        <SectionCard
          title="Billing Agreement"
          action={
            <Button variant="secondary" onClick={() => setAgreementsOpen(true)}>
              Ver acordos de pagamento
            </Button>
          }
        >
          <p className="text-(--color-text-secondary) text-sm italic">
            Nenhuma Billing Agreement atual neste contrato.
          </p>
        </SectionCard>
      )}

      <ContractBillingAgreementsModal
        contractId={contractId}
        currentBillingAgreementId={ba?.id ?? null}
        open={agreementsOpen}
        onClose={() => setAgreementsOpen(false)}
      />

      <Modal
        open={selectedInvoice !== null}
        onClose={() => setSelectedInvoice(null)}
        title="Resumo da Fatura"
        size="lg"
      >
        {selectedInvoice && <InvoiceSummary invoice={selectedInvoice} />}
      </Modal>

      {settlingInvoice && (
        <ManualSettlementModal
          open
          onClose={() => setSettlingInvoice(null)}
          invoice={settlingInvoice}
          invalidateKey={['sales', 'contract', contractId]}
        />
      )}
    </div>
  );
}
